import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

type Tema = 'claro' | 'escuro' | 'sistema';

const CHAVE = '@rondonbens:tema';

interface TemaCtx {
  tema: Tema;
  setTema: (t: Tema) => void;
  /** O que está efetivamente na tela — resolve 'sistema'. */
  efetivo: 'claro' | 'escuro';
}

const Ctx = createContext<TemaCtx | null>(null);

function preferenciaDoSistema(): 'claro' | 'escuro' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'escuro' : 'claro';
}

export function TemaProvider({ children }: { children: ReactNode }) {
  const [tema, setTemaEstado] = useState<Tema>(
    () => (localStorage.getItem(CHAVE) as Tema | null) ?? 'sistema',
  );
  const [efetivo, setEfetivo] = useState<'claro' | 'escuro'>(() =>
    tema === 'sistema' ? preferenciaDoSistema() : tema,
  );

  useEffect(() => {
    const resolver = () => {
      const alvo = tema === 'sistema' ? preferenciaDoSistema() : tema;
      setEfetivo(alvo);
      const raiz = document.documentElement;
      // 'sistema' remove o atributo e deixa o CSS decidir pela media query —
      // fixar um valor aqui quebraria a troca automática ao anoitecer.
      if (tema === 'sistema') raiz.removeAttribute('data-theme');
      else raiz.setAttribute('data-theme', tema === 'escuro' ? 'dark' : 'light');
    };

    resolver();
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', resolver);
    return () => mq.removeEventListener('change', resolver);
  }, [tema]);

  const setTema = (t: Tema) => {
    localStorage.setItem(CHAVE, t);
    setTemaEstado(t);
  };

  return <Ctx.Provider value={{ tema, setTema, efetivo }}>{children}</Ctx.Provider>;
}

export function useTema(): TemaCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTema precisa estar dentro de TemaProvider');
  return ctx;
}
